// Sentry loader. Lazily imports @sentry/bun (preferred for Bun apps) and
// falls back to @sentry/node (works in both Node and Bun via Node-compat).
// The caller does NOT need either installed — absence is treated as
// "Sentry disabled" rather than an error.

import { readEnv } from './env.js';
import type { ObservabilityOptions, SentrySdk } from './types.js';

let sdk: SentrySdk | null = null;
let initAttempted = false;

/**
 * Load + init Sentry. No-op when DSN is unset or the package is missing.
 * Idempotent: subsequent calls return the existing handle.
 */
export async function initSentry(opts: ObservabilityOptions): Promise<SentrySdk | null> {
  if (sdk || initAttempted) return sdk;
  initAttempted = true;

  const dsn = opts.sentryDsn ?? readEnv('SENTRY_DSN');
  if (!dsn) return null;

  const loaded = await tryImportSentry();
  if (!loaded) {
    console.warn(
      '[observability] SENTRY_DSN set but @sentry/bun / @sentry/node not installed; ' +
        'install one to enable error reporting',
    );
    return null;
  }

  loaded.init({
    dsn,
    environment: opts.environment ?? readEnv('NODE_ENV') ?? 'dev',
    release: opts.appVersion,
    // tracesSampleRate handled separately by OTEL path; Sentry's own
    // performance monitoring stays off to avoid double-counting.
    tracesSampleRate: 0,
    defaultIntegrations: false,
  });
  sdk = loaded;
  return sdk;
}

async function tryImportSentry(): Promise<SentrySdk | null> {
  // Try @sentry/bun first — native integration, smaller surface.
  // Then fall back to @sentry/node for Node-compat environments.
  for (const spec of ['@sentry/bun', '@sentry/node'] as const) {
    try {
      const mod = (await import(/* @vite-ignore */ spec)) as Partial<SentrySdk>;
      if (mod && typeof mod.init === 'function' && typeof mod.captureException === 'function') {
        return mod as SentrySdk;
      }
    } catch {
      // not installed; try next
    }
  }
  return null;
}

/** Report an error. No-op when Sentry is not initialized. */
export function captureSentryError(err: unknown, ctx?: Record<string, unknown>): void {
  if (!sdk) return;
  try {
    sdk.captureException(err, ctx);
  } catch {
    // never let observability itself crash the call site
  }
}

export async function shutdownSentry(): Promise<void> {
  if (!sdk) return;
  try {
    await sdk.flush(2000);
  } catch {
    // swallow
  }
  sdk = null;
  initAttempted = false;
}
